import { useEffect, useRef, useState } from "react";
import { APIProvider, Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { LocateFixed, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Mirganj, Bihar (approx) — default centre when no coords yet
const DEFAULT_CENTER = { lat: 26.3855, lng: 84.1861 };

function round(n) {
  return Math.round(Number(n) * 1e5) / 1e5;
}

function PickerControls({ value, onChange, height }) {
  const map = useMap();
  const geoLib = useMapsLibrary("geocoding");
  const placesLib = useMapsLibrary("places");
  const geocoderRef = useRef(null);
  const inputRef = useRef(null);
  const lastRef = useRef(null);

  useEffect(() => {
    if (geoLib) geocoderRef.current = new geoLib.Geocoder();
  }, [geoLib]);

  const reverse = (lat, lng) => {
    const key = `${round(lat)},${round(lng)}`;
    if (lastRef.current === key) return;
    lastRef.current = key;
    if (!geocoderRef.current) {
      onChange({ lat, lng, address: "" });
      return;
    }
    geocoderRef.current.geocode({ location: { lat, lng } }, (res, status) => {
      const address = status === "OK" && res && res[0] ? res[0].formatted_address : "";
      onChange({ lat, lng, address });
    });
  };

  // Reverse-geocode the map centre whenever the user finishes moving the map.
  useEffect(() => {
    if (!map) return undefined;
    const listener = map.addListener("idle", () => {
      const c = map.getCenter();
      if (c) reverse(c.lat(), c.lng());
    });
    return () => window.google?.maps?.event?.removeListener(listener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, geoLib]);

  // Places autocomplete on the search box.
  useEffect(() => {
    if (!placesLib || !inputRef.current || !map) return undefined;
    const ac = new placesLib.Autocomplete(inputRef.current, {
      fields: ["geometry"],
      componentRestrictions: { country: "in" },
    });
    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (place.geometry?.location) {
        map.panTo(place.geometry.location);
        map.setZoom(17);
      }
    });
    return () => window.google?.maps?.event?.removeListener(listener);
  }, [placesLib, map]);

  const locate = () => {
    if (!navigator.geolocation) {
      toast.error("Location not supported on this device");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!map) return;
        map.panTo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        map.setZoom(17);
      },
      () => toast.error("Could not get your location. Please allow permission."),
    );
  };

  return (
    <>
      <input
        ref={inputRef}
        data-testid="map-search"
        placeholder="Search area, landmark or address…"
        className="absolute left-2 right-2 top-2 z-10 min-h-10 rounded-full border border-stone-300 bg-white/95 px-4 text-sm shadow-md outline-none focus:border-orange-500"
      />
      <button
        type="button"
        onClick={locate}
        data-testid="map-locate"
        className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1 rounded-full bg-white px-3 py-2 text-xs font-semibold text-stone-800 shadow-md hover:text-orange-700"
      >
        <LocateFixed size={13} /> Current
      </button>
      {/* fixed centre pin */}
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-full text-orange-600"
        style={{ height }}
      />
    </>
  );
}

export function MapPicker({ value, onChange, height = 260 }) {
  const [apiKey, setApiKey] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/config/maps-key")
      .then((r) => setApiKey(r.data.key || ""))
      .catch(() => setApiKey(""))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) {
    return (
      <div
        className="animate-pulse rounded-2xl bg-stone-100"
        style={{ height }}
        data-testid="map-loading"
      />
    );
  }

  if (!apiKey) {
    return (
      <div
        className="rounded-2xl border border-stone-200 bg-stone-50 p-4 text-sm text-stone-600"
        data-testid="map-unavailable"
      >
        Map is unavailable right now — please type your full delivery address below.
      </div>
    );
  }

  const center = value?.lat && value?.lng ? { lat: value.lat, lng: value.lng } : DEFAULT_CENTER;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-stone-200" data-testid="map-picker">
      <APIProvider apiKey={apiKey}>
        <Map
          style={{ width: "100%", height }}
          defaultCenter={center}
          defaultZoom={16}
          gestureHandling="greedy"
          disableDefaultUI
          zoomControl
          clickableIcons={false}
        />
        <PickerControls value={value} onChange={onChange} height={height} />
      </APIProvider>
      {/* centre pin overlay (outside APIProvider so it always renders on top) */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-full">
        <MapPin className="h-8 w-8 fill-orange-600 text-white drop-shadow-md" strokeWidth={1.5} />
      </div>
    </div>
  );
}
